import React, { useState, useEffect } from "react";
import Cookies from "js-cookie";
import config from "../../config";
import styles from "../css/css_pages/TSOExpensesManagement.module.css";
import {
  FiDollarSign,
  FiEye,
  FiX,
  FiRefreshCw,
  FiSearch,
  FiUser,
  FiAlertCircle,
  FiCheckCircle,
  FiTruck,
  FiMapPin,
} from "react-icons/fi";

const TSOTransportManagement = () => {
  const [transportRecords, setTransportRecords] = useState([]);
  const [filteredRecords, setFilteredRecords] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);

  const [statusFilter, setStatusFilter] = useState("");
  const [transportModeFilter, setTransportModeFilter] = useState("");
  const [tsoNameFilter, setTsoNameFilter] = useState("");
  const [expenseFilter, setExpenseFilter] = useState({ min: "", max: "" });
  const [monthFilter, setMonthFilter] = useState("");
  const [scheduleNoFilter, setScheduleNoFilter] = useState("");

  useEffect(() => {
    fetchTransportRecords();
  }, []);

  useEffect(() => {
    filterRecords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    transportRecords,
    searchTerm,
    statusFilter,
    transportModeFilter,
    tsoNameFilter,
    expenseFilter,
    monthFilter,
    scheduleNoFilter,
  ]);

  const filterRecords = () => {
    let filtered = [...transportRecords];

    if (searchTerm) {
      const term = searchTerm.toLowerCase().trim();
      filtered = filtered.filter((record) => {
        const tsoName = (record.tso_name || "").toLowerCase();
        const transportMode = (record.transport_mode || "").toLowerCase();
        const scheduleId = (record.schedule_id || "").toLowerCase();
        return (
          tsoName.includes(term) ||
          transportMode.includes(term) ||
          scheduleId.includes(term)
        );
      });
    }

    if (statusFilter) {
      const isActive = statusFilter === "active";
      filtered = filtered.filter((record) => record.status === isActive);
    }

    if (transportModeFilter) {
      filtered = filtered.filter(
        (record) => record.transport_mode === transportModeFilter
      );
    }

    if (tsoNameFilter) {
      filtered = filtered.filter((record) =>
        (record.tso_name || "")
          .toLowerCase()
          .includes(tsoNameFilter.toLowerCase())
      );
    }

    if (expenseFilter.min || expenseFilter.max) {
      filtered = filtered.filter((record) => {
        const expense = parseFloat(record.travelling_expenses) || 0;
        const min = parseFloat(expenseFilter.min) || 0;
        const max = parseFloat(expenseFilter.max) || Infinity;
        return expense >= min && expense <= max;
      });
    }

    if (monthFilter) {
      filtered = filtered.filter((record) => {
        if (!record.scheduled_date) return false;
        const recordDate = new Date(record.scheduled_date);
        const recordMonth = `${recordDate.getFullYear()}-${String(
          recordDate.getMonth() + 1
        ).padStart(2, "0")}`;
        return recordMonth === monthFilter;
      });
    }

    if (scheduleNoFilter) {
      filtered = filtered.filter((record) =>
        String(record.schedule_no || "")
          .toLowerCase()
          .includes(scheduleNoFilter.toLowerCase())
      );
    }

    // Sort by scheduled_date in descending order (newest first)
    filtered.sort((a, b) => {
      const dateA = a.scheduled_date ? new Date(a.scheduled_date) : new Date(0);
      const dateB = b.scheduled_date ? new Date(b.scheduled_date) : new Date(0);
      return dateB - dateA;
    });

    setFilteredRecords(filtered);
  };

  const formatDateTime = (dateString, showOnlyTime = false) => {
    if (!dateString || isNaN(Date.parse(dateString))) {
      return "Invalid Date";
    }

    const date = new Date(dateString);

    // Format as UTC time
    const options = {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
      timeZone: "UTC", // Use UTC instead of local time
    };

    if (!showOnlyTime) {
      options.year = "numeric";
      options.month = "2-digit";
      options.day = "2-digit";
    }

    return date.toLocaleString("en-US", options).replace(",", "");
  };

  const fetchTransportRecords = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const token = Cookies.get("authToken");
      if (!token) throw new Error("Authentication token not found");

      const response = await fetch(
        `${config.API_URL}/management/get-all-tso-expenses`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) throw new Error("Failed to fetch transport records");

      const data = await response.json();
      if (Array.isArray(data)) {
        setTransportRecords(data);
        setFilteredRecords(data);
      } else {
        setTransportRecords([]);
        setFilteredRecords([]);
      }
    } catch (error) {
      console.error("Error fetching transport records:", error);
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchTransportRecordDetails = async (recordId) => {
    setIsLoading(true);
    try {
      const token = Cookies.get("authToken");
      if (!token) throw new Error("Authentication token not found");

      const response = await fetch(
        `${config.API_URL}/management/get-tso-expense-by-id/${recordId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok)
        throw new Error("Failed to fetch transport record details");

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error fetching transport record details:", error);
      setError(error.message);
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewDetails = async (record) => {
    const detailedRecord = await fetchTransportRecordDetails(record.id);
    if (detailedRecord) {
      setSelectedRecord(detailedRecord);
      setShowDetailModal(true);
    }
  };

  const closeDetailModal = () => {
    setShowDetailModal(false);
    setSelectedRecord(null);
  };

  const formatCurrency = (amount) => {
    const numAmount = parseFloat(amount) || 0;
    return new Intl.NumberFormat("en-LK", {
      style: "currency",
      currency: "LKR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
      .format(numAmount)
      .replace("LKR", "Rs.");
  };

  const getStatusColor = (status) => {
    return status ? styles.statusApproved : styles.statusRejected;
  };

  const getStatusText = (status) => {
    return status ? "Active" : "Inactive";
  };

  const getTotalExpenses = () => {
    return filteredRecords.reduce(
      (total, record) => total + (parseFloat(record.travelling_expenses) || 0),
      0
    );
  };

  const getTotalDistance = () => {
    return filteredRecords.reduce(
      (total, record) => total + (parseFloat(record.distance_travelled) || 0),
      0
    );
  };

  const clearFilters = () => {
    setSearchTerm("");
    setStatusFilter("");
    setTransportModeFilter("");
    setTsoNameFilter("");
    setExpenseFilter({ min: "", max: "" });
    setMonthFilter("");
    setScheduleNoFilter("");
  };

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 8000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  if (isLoading && !showDetailModal) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingSpinner} />
        <p>Loading transport records...</p>
      </div>
    );
  }

  return (
    <div className={styles.expensesManagement}>
      <header className={styles.managementHeader}>
        <div className={styles.headerTitleRow}>
          <h1>TSO Transport Management</h1>
          <div className={styles.headerActions}>
            <button
              className={styles.refreshButton}
              onClick={fetchTransportRecords}
              disabled={isLoading}
            >
              <FiRefreshCw className={isLoading ? styles.refreshing : ""} />
            </button>
          </div>
        </div>

        <div className={styles.summaryCards}>
          <div className={styles.summaryCard}>
            <div className={styles.cardIcon}>
              <FiDollarSign />
            </div>
            <div className={styles.cardContent}>
              <h3>Total Expenses</h3>
              <p className={styles.totalAmount}>
                {formatCurrency(getTotalExpenses())}
              </p>
            </div>
          </div>
          <div className={styles.summaryCard}>
            <div className={styles.cardIcon}>
              <FiMapPin />
            </div>
            <div className={styles.cardContent}>
              <h3>Total Distance</h3>
              <p className={styles.totalCount}>{getTotalDistance()} km</p>
            </div>
          </div>
          <div className={styles.summaryCard}>
            <div className={styles.cardIcon}>
              <FiTruck />
            </div>
            <div className={styles.cardContent}>
              <h3>Total Records</h3>
              <p className={styles.totalCount}>{filteredRecords.length}</p>
            </div>
          </div>
        </div>

        <div className={styles.filtersContainer}>
          <div className={styles.searchBar}>
            <FiSearch className={styles.searchIcon} />
            <input
              type="text"
              placeholder="Search by TSO name, transport mode, or schedule ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className={styles.filterControls}>
            <div className={styles.filterGroup}>
              <label>Status:</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>

            <div className={styles.filterGroup}>
              <label>Transport Mode:</label>
              <select
                value={transportModeFilter}
                onChange={(e) => setTransportModeFilter(e.target.value)}
              >
                <option value="">All Modes</option>
                <option value="personal">Personal</option>
                <option value="public">Public</option>
                <option value="company">Company</option>
              </select>
            </div>

            <div className={styles.filterGroup}>
              <label>TSO Name:</label>
              <input
                type="text"
                placeholder="Filter by TSO name"
                value={tsoNameFilter}
                onChange={(e) => setTsoNameFilter(e.target.value)}
              />
            </div>

            <div className={styles.filterGroup}>
              <label>Expense Range:</label>
              <div className={styles.amountRange}>
                <input
                  type="number"
                  placeholder="Min"
                  value={expenseFilter.min}
                  onChange={(e) =>
                    setExpenseFilter((prev) => ({
                      ...prev,
                      min: e.target.value,
                    }))
                  }
                />
                <span>-</span>
                <input
                  type="number"
                  placeholder="Max"
                  value={expenseFilter.max}
                  onChange={(e) =>
                    setExpenseFilter((prev) => ({
                      ...prev,
                      max: e.target.value,
                    }))
                  }
                />
              </div>
            </div>

            <div className={styles.filterGroup}>
              <label>Month:</label>
              <input
                type="month"
                value={monthFilter}
                onChange={(e) => setMonthFilter(e.target.value)}
              />
            </div>

            <div className={styles.filterGroup}>
              <label>Schedule No:</label>
              <input
                type="text"
                placeholder="Filter by schedule no"
                value={scheduleNoFilter}
                onChange={(e) => setScheduleNoFilter(e.target.value)}
              />
            </div>

            <button
              className={styles.clearFiltersButton}
              onClick={clearFilters}
            >
              Clear Filters
            </button>
          </div>
        </div>
      </header>

      {error && (
        <div className={`${styles.notification} ${styles.error}`}>
          <FiAlertCircle />
          <span>{error}</span>
          <button onClick={() => setError(null)}>
            <FiX />
          </button>
        </div>
      )}

      {success && (
        <div className={`${styles.notification} ${styles.success}`}>
          <FiCheckCircle />
          <span>{success}</span>
          <button onClick={() => setSuccess(null)}>
            <FiX />
          </button>
        </div>
      )}

      <div className={styles.expensesContainer}>
        {filteredRecords.length > 0 ? (
          <div className={styles.expensesTable}>
            <table>
              <thead>
                <tr>
                  <th>TSO Name</th>
                  <th>Transport Mode</th>
                  <th>Distance (km)</th>
                  <th>Expenses</th>
                  <th>Status</th>
                  <th>Client No</th>
                  <th>Schedule No</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredRecords.map((record) => (
                  <tr key={record.id}>
                    <td className={styles.descriptionCell}>
                      <div className={styles.expenseDescription}>
                        <FiUser className={styles.inlineIcon} />
                        {record.tso_name}
                      </div>
                    </td>
                    <td>
                      <span className={styles.category}>
                        <FiTruck className={styles.inlineIcon} />
                        {record.transport_mode}
                      </span>
                    </td>
                    <td className={styles.amountCell}>
                      <span className={styles.amount}>
                        <FiMapPin className={styles.inlineIcon} />
                        {record.distance_travelled} km
                      </span>
                    </td>
                    <td className={styles.amountCell}>
                      <span className={styles.amount}>
                        {formatCurrency(record.travelling_expenses)}
                      </span>
                    </td>
                    <td>
                      <span
                        className={`${styles.status} ${getStatusColor(
                          record.status
                        )}`}
                      >
                        {getStatusText(record.status)}
                      </span>
                    </td>
                    <td className={styles.referenceCell}>
                      {record.customer_id || "N/A"}
                    </td>
                    <td className={styles.referenceCell}>
                      {record.schedule_no || "N/A"}
                    </td>
                    <td>
                      <div className={styles.actions}>
                        <button
                          className={`${styles.actionButton} ${styles.view}`}
                          onClick={() => handleViewDetails(record)}
                          title="View Details"
                          disabled={isLoading}
                        >
                          <FiEye />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className={styles.noExpenses}>
            <FiTruck size={64} />
            <h3>No transport records found</h3>
            <p>No transport records match your current filters</p>
          </div>
        )}
      </div>

      {showDetailModal && selectedRecord && (
        <div className={styles.modalOverlay} onClick={closeDetailModal}>
          <div
            className={styles.detailModalContent}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.modalHeader}>
              <h2>Transport Record Details</h2>
              <button className={styles.closeButton} onClick={closeDetailModal}>
                <FiX />
              </button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.expenseDetails}>
                <div className={styles.detailRow}>
                  <label>Record ID:</label>
                  <span>{selectedRecord.id || "-"}</span>
                </div>
                <div className={styles.detailRow}>
                  <label>Customer Name:</label>
                  <span>{selectedRecord.customer_id || "-"}</span>
                </div>
                <div className={styles.detailRow}>
                  <label>TSO ID:</label>
                  <span>{selectedRecord.tso_id || "-"}</span>
                </div>
                <div className={styles.detailRow}>
                  <label>Transport Mode:</label>
                  <span>{selectedRecord.transport_mode || "-"}</span>
                </div>
                <div className={styles.detailRow}>
                  <label>Distance Travelled:</label>
                  <span>{selectedRecord.distance_travelled || "0"} km</span>
                </div>
                <div className={styles.detailRow}>
                  <label>Travel Expenses:</label>
                  <span className={styles.detailAmount}>
                    {formatCurrency(selectedRecord.travelling_expenses)}
                  </span>
                </div>
                <div className={styles.detailRow}>
                  <label>Status:</label>
                  <span
                    className={`${styles.status} ${getStatusColor(
                      selectedRecord.status
                    )}`}
                  >
                    {getStatusText(selectedRecord.status)}
                  </span>
                </div>
                <div className={styles.detailRow}>
                  <label>Schedule ID:</label>
                  <span>{selectedRecord.schedule_id || "-"}</span>
                </div>
                <div className={styles.detailRow}>
                  <label>Schedule Number:</label>
                  <span>{selectedRecord.schedule_no || "-"}</span>
                </div>
                <div className={styles.detailRow}>
                  <label>Schedule Mode:</label>
                  <span className={styles.category}>
                    {selectedRecord.schedule_mode || "-"}
                  </span>
                </div>
                <div className={styles.detailRow}>
                  <label>Scheduled Date:</label>
                  <span>{formatDateTime(selectedRecord.scheduled_date)}</span>
                </div>
                <div className={styles.detailRow}>
                  <label>Created Date:</label>
                  <span>{formatDateTime(selectedRecord.created_date)}</span>
                </div>
                <div className={styles.detailRow}>
                  <label>Last Updated:</label>
                  <span>{formatDateTime(selectedRecord.updated_date)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TSOTransportManagement;
